import { useState } from 'react';
import { addWarehouse, deleteWarehouse, fetchWarehouses, updateWarehouse } from '@/api/warehouses';
import { fetchPostcodeDetails } from '@/api/postcode';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { TextField } from '@/components/ui/TextField';
import { Toggle } from '@/components/ui/Toggle';
import { Badge } from '@/components/ui/Badge';
import { ErrorState, LoadingState } from '@/components/feedback/StateViews';
import { useAsyncData } from '@/hooks/useAsyncData';
import { toast } from '@/store/useToastStore';
import { errorMessageOf } from '@/lib/errors';
import type { Warehouse, WarehouseInput } from '@/types';

function emptyWarehouse(): WarehouseInput {
  return {
    name: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    addressLine1: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India',
    isDefault: false,
    isActive: true,
  };
}

function isValidEmail(value: string): boolean {
  return value.trim().length === 0 || value.includes('@');
}

function isValidPhone(value: string): boolean {
  return value.trim().length === 0 || /^\d{10}$/.test(value.trim());
}

export function WarehousesSection() {
  const { data, errorMessage, isLoading, reload } = useAsyncData<Warehouse[] | null>(
    (signal) => fetchWarehouses(signal),
    [],
  );

  const [draft, setDraft] = useState<{ id: string | null; input: WarehouseInput } | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [pincodeStatus, setPincodeStatus] = useState<'idle' | 'loading' | 'error'>('idle');

  function openCreate() {
    setDraft({ id: null, input: emptyWarehouse() });
    setPincodeStatus('idle');
  }

  function openEdit(warehouse: Warehouse) {
    setDraft({
      id: warehouse.id,
      input: {
        name: warehouse.name,
        contactName: warehouse.contactName ?? '',
        contactPhone: warehouse.contactPhone ?? '',
        contactEmail: warehouse.contactEmail ?? '',
        addressLine1: warehouse.addressLine1 ?? warehouse.line1 ?? '',
        city: warehouse.city,
        state: warehouse.state,
        pincode: warehouse.pincode,
        country: warehouse.country ?? 'India',
        isDefault: warehouse.isDefault,
        isActive: warehouse.isActive ?? true,
      },
    });
    setPincodeStatus('idle');
  }

  async function handleSave() {
    if (!draft) {
      return;
    }
    setIsBusy(true);
    try {
      if (draft.id) {
        await updateWarehouse(draft.id, draft.input);
      } else {
        await addWarehouse(draft.input);
      }
      toast.success('Warehouse saved');
      setDraft(null);
      reload();
    } catch (error) {
      toast.error(errorMessageOf(error));
    } finally {
      setIsBusy(false);
    }
  }

  async function handleDelete(warehouse: Warehouse) {
    try {
      await deleteWarehouse(warehouse.id);
      toast.success('Warehouse deleted');
      reload();
    } catch (error) {
      toast.error(errorMessageOf(error));
    }
  }

  if (isLoading) {
    return <LoadingState message="Loading warehouses…" />;
  }
  if (errorMessage) {
    return <ErrorState message={errorMessage} onRetry={reload} />;
  }

  const warehouses = data ?? [];

  function updateInput(patch: Partial<WarehouseInput>) {
    setDraft((current) => (current ? { ...current, input: { ...current.input, ...patch } } : current));
  }

  async function handlePincodeChange(rawValue: string) {
    const digits = rawValue.replace(/\D/g, '').slice(0, 6);
    if (digits.length < 6) {
      updateInput({ pincode: digits, city: '', state: '' });
      setPincodeStatus('idle');
      return;
    }
    updateInput({ pincode: digits });
    setPincodeStatus('loading');
    try {
      const details = await fetchPostcodeDetails(digits);
      updateInput({ city: details.city, state: details.state });
      setPincodeStatus('idle');
    } catch (error) {
      updateInput({ city: '', state: '' });
      setPincodeStatus('error');
      toast.error(errorMessageOf(error));
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-ink">Warehouses</h2>
            <p className="mt-0.5 text-sm text-ink-muted">Return destinations for reverse pickups.</p>
          </div>
          <Button onClick={openCreate}>Add warehouse</Button>
        </div>
        {warehouses.length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-muted">No warehouses configured.</p>
        ) : (
          <ul className="mt-4 divide-y divide-line">
            {warehouses.map((warehouse) => (
              <li key={warehouse.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-ink">{warehouse.name}</span>
                    {warehouse.isDefault ? <Badge tone="success">Default</Badge> : null}
                    {warehouse.shiprocketWarehouse ? <Badge tone="neutral">Shiprocket</Badge> : null}
                    {warehouse.isActive === false ? <Badge tone="danger">Inactive</Badge> : null}
                  </div>
                  <span className="text-xs text-ink-subtle">
                    {[warehouse.city, warehouse.state, warehouse.pincode].filter(Boolean).join(', ')}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="secondary" onClick={() => openEdit(warehouse)}>
                    Edit
                  </Button>
                  {!warehouse.isDefault ? (
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(warehouse)}>
                      Delete
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Modal
        isOpen={draft !== null}
        onClose={() => setDraft(null)}
        title={draft?.id ? 'Edit warehouse' : 'Add warehouse'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDraft(null)} disabled={isBusy}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              isLoading={isBusy}
              disabled={
                !draft ||
                !draft.input.name.trim() ||
                !isValidEmail(draft.input.contactEmail ?? '') ||
                !isValidPhone(draft.input.contactPhone ?? '') ||
                pincodeStatus === 'loading'
              }
            >
              Save
            </Button>
          </>
        }
      >
        {draft ? (
          <div className="flex flex-col gap-3">
            <TextField
              label="Name"
              value={draft.input.name}
              maxLength={80}
              onChange={(event) => updateInput({ name: event.target.value })}
            />
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label="Contact name"
                value={draft.input.contactName ?? ''}
                maxLength={50}
                onChange={(event) => updateInput({ contactName: event.target.value })}
              />
              <TextField
                label="Contact phone"
                value={draft.input.contactPhone ?? ''}
                maxLength={10}
                inputMode="numeric"
                errorText={
                  draft.input.contactPhone && !isValidPhone(draft.input.contactPhone)
                    ? 'Enter a 10 digit phone number'
                    : undefined
                }
                onChange={(event) => updateInput({ contactPhone: event.target.value.replace(/\D/g, '').slice(0, 10) })}
              />
            </div>
            <TextField
              label="Contact email"
              type="email"
              value={draft.input.contactEmail ?? ''}
              maxLength={200}
              errorText={
                draft.input.contactEmail && !isValidEmail(draft.input.contactEmail) ? 'Enter a valid email' : undefined
              }
              onChange={(event) => updateInput({ contactEmail: event.target.value })}
            />
            <TextField
              label="Address"
              value={draft.input.addressLine1 ?? ''}
              maxLength={400}
              onChange={(event) => updateInput({ addressLine1: event.target.value })}
            />
            <div className="grid grid-cols-3 gap-3">
              <TextField
                label="Pincode"
                value={draft.input.pincode}
                maxLength={6}
                inputMode="numeric"
                hint={pincodeStatus === 'loading' ? 'Looking up city & state…' : undefined}
                errorText={pincodeStatus === 'error' ? 'Could not find that pincode' : undefined}
                onChange={(event) => handlePincodeChange(event.target.value)}
              />
              <TextField
                label="City"
                value={draft.input.city}
                disabled
                className="disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-ink-subtle"
              />
              <TextField
                label="State"
                value={draft.input.state}
                disabled
                className="disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-ink-subtle"
              />
            </div>
            <Toggle
              label="Set as default warehouse"
              checked={Boolean(draft.input.isDefault)}
              onChange={(checked) => updateInput({ isDefault: checked })}
            />
            <Toggle
              label="Active"
              checked={draft.input.isActive ?? true}
              onChange={(checked) => updateInput({ isActive: checked })}
            />
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
